import Add from '@react-spectrum/s2/icons/Add';
import HelpCircle from '@react-spectrum/s2/icons/HelpCircle';
import Settings from '@react-spectrum/s2/icons/Settings';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useUserSettings } from '@/hooks/useUserSettings.ts';
import { useChatThreads } from '@/spaces/chat/useChatThreads.ts';
import { cn } from '@/utils/cn.ts';
import { trackEvent } from '@/utils/fullstory';

import type { Space } from '../../../../bridge/types/state.ts';

import { NAV_ITEMS } from './navItems.ts';

type NavTarget = Space['type'] | 'chat';

const INDICATOR_H = 20; // px — matches h-5
const PHASE1_MS = 220; // leading edge stretches to destination
const PHASE2_MS = 280; // trailing edge contracts to destination
const PHASE1_EASE = 'cubic-bezier(0.3, 0, 0.5, 1)'; // accelerates out, smooth arrival
const PHASE2_EASE = 'cubic-bezier(0, 0, 0.15, 1)'; // strong ease-out, organic settle

const NavTooltip = ({ label }: { label: string }) => (
	<span
		className="pointer-events-none absolute left-full ml-1 rounded-md
			bg-gray-800 px-2 py-1 text-sm whitespace-nowrap text-white opacity-0
			shadow-md transition-opacity group-hover:opacity-100"
	>
		{label}
	</span>
);

export const NavBar = () => {
	const bridgeApi = useBridgeApi();
	const currentSpaceType = bridgeApi.user_state?.current_space?.type ?? null;
	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;
	const isCurrentChatEmpty =
		currentChatId !== null &&
		(bridgeApi.user_state?.chats?.[currentChatId]?.messages.length ?? 0) === 0;
	const { threads } = useChatThreads();
	const hasUnreadThreads = useMemo(
		() => threads.some((t) => t.is_unread),
		[threads],
	);

	// Square withdrew our access, which only the merchant can undo. Safe to read here: NavBar
	// renders inside App, which main.tsx mounts under UserSettingsProvider.
	const { settings } = useUserSettings();
	const squareNeedsAttention = !!settings?.square_connection?.square_revoked_at;

	// Leaving the Brand space implicitly marks the onboarding-generated business
	// profile as reviewed — the user has had their chance to look it over. Read
	// straight from user_state; only fires the one-time flip while it's still
	// pending (profile ready but not yet reviewed).
	const maybeMarkProfileReviewed = useCallback(() => {
		const onboarding = bridgeApi.user_state?.onboarding_status;
		if (
			currentSpaceType === 'brand' &&
			onboarding?.business_profile_ready === true &&
			onboarding?.business_profile_reviewed !== true
		) {
			bridgeApi.sendAction({ type: 'do_mark_business_profile_reviewed' });
		}
	}, [bridgeApi, currentSpaceType]);

	const handleNavClick = useCallback(
		(name: NavTarget) => {
			if (name !== 'brand') maybeMarkProfileReviewed();
			trackEvent('Space Navigated', { space_type: name });
			bridgeApi.sendAction({
				type: 'do_navigate_to_space',
				space_type: name,
			});
		},
		[bridgeApi, maybeMarkProfileReviewed],
	);

	const enabledItems = NAV_ITEMS.filter((item) => !item.isDisabled);

	// Refs for the floating indicator and all nav buttons
	const indicatorRef = useRef<HTMLDivElement>(null);
	const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
	const isFirstRender = useRef(true);

	const activeKey = currentSpaceType ?? 'chat';

	useLayoutEffect(() => {
		const indicator = indicatorRef.current;
		const button = buttonRefs.current.get(activeKey);
		if (!indicator || !button) return;

		const navEl = indicator.parentElement;
		if (!navEl) return;

		const navRect = navEl.getBoundingClientRect();
		const btnRect = button.getBoundingClientRect();
		const newTop =
			btnRect.top - navRect.top + (btnRect.height - INDICATOR_H) / 2;

		// First render: snap into place, no animation
		if (isFirstRender.current) {
			isFirstRender.current = false;
			indicator.style.transition = 'none';
			indicator.style.top = `${newTop}px`;
			indicator.style.height = `${INDICATOR_H}px`;
			indicator.style.opacity = '1';
			return;
		}

		// Read current rendered position before canceling any in-flight transition
		const fromTop = parseFloat(getComputedStyle(indicator).top);
		if (isNaN(fromTop) || Math.abs(newTop - fromTop) < 1) return;

		// Lock the indicator at its current visual position and cancel the transition
		indicator.style.transition = 'none';
		indicator.style.top = `${fromTop}px`;
		indicator.style.height = `${INDICATOR_H}px`;
		void indicator.offsetHeight; // commit

		const goingDown = newTop > fromTop;
		const stretchHeight = Math.abs(newTop - fromTop) + INDICATOR_H;

		// Phase 1: leading edge animates to destination; trailing edge holds still
		if (goingDown) {
			// top stays at fromTop, bottom extends down to newTop + INDICATOR_H
			indicator.style.transition = `height ${PHASE1_MS}ms ${PHASE1_EASE}`;
			indicator.style.height = `${stretchHeight}px`;
		} else {
			// bottom stays at fromTop + INDICATOR_H, top rises up to newTop
			indicator.style.transition = `top ${PHASE1_MS}ms ${PHASE1_EASE}, height ${PHASE1_MS}ms ${PHASE1_EASE}`;
			indicator.style.top = `${newTop}px`;
			indicator.style.height = `${stretchHeight}px`;
		}

		// Phase 2: trailing edge contracts — triggered once phase 1 completes
		let rafId: number;
		const onPhase1End = (e: TransitionEvent) => {
			if (e.target !== indicator || e.propertyName !== 'height') return;
			indicator.removeEventListener('transitionend', onPhase1End);
			rafId = requestAnimationFrame(() => {
				indicator.style.transition = `top ${PHASE2_MS}ms ${PHASE2_EASE}, height ${PHASE2_MS}ms ${PHASE2_EASE}`;
				indicator.style.top = `${newTop}px`;
				indicator.style.height = `${INDICATOR_H}px`;
			});
		};
		indicator.addEventListener('transitionend', onPhase1End);

		return () => {
			indicator.removeEventListener('transitionend', onPhase1End);
			cancelAnimationFrame(rafId);
		};
	}, [activeKey]);

	const navBtnClass = (isActive: boolean) =>
		cn(
			`group relative flex h-12 w-12 items-center justify-center
			text-(--text-primary)
			[&_svg]:block [&_svg]:shrink-0
			before:absolute before:left-0 before:top-1/2 before:h-5 before:w-0.5
			before:-translate-y-1/2 before:rounded-full
			before:transition-opacity`,
			isActive
				? 'before:opacity-0' // floating div owns the active indicator
				: `cursor-pointer before:bg-gray-400 before:opacity-0
					hover:before:opacity-100`,
		);

	return (
		<nav
			className="relative z-20 flex h-full w-16 flex-shrink-0 flex-col
				items-center gap-2 py-4"
		>
			{/* Floating gooey active indicator — two-phase animated via useLayoutEffect */}
			<div
				ref={indicatorRef}
				className="pointer-events-none absolute left-2 w-0.5 rounded-full
					bg-(--text-primary) opacity-0"
				style={{ top: 0, height: INDICATOR_H }}
			/>

			<button
				type="button"
				onClick={() => {
					if (currentSpaceType === null && isCurrentChatEmpty) return;
					// Starting a new chat also leaves the current space.
					maybeMarkProfileReviewed();
					trackEvent('Chat Created');
					bridgeApi.sendAction({ type: 'do_create_chat' });
				}}
				aria-label="New chat"
				data-testid="nav-new-chat"
				className="group relative flex h-12 w-12 cursor-pointer items-center
					justify-center [--iconPrimary:white]"
			>
				<span
					className="flex h-7 w-7 items-center justify-center rounded-full
						bg-(--text-primary) transition-all group-hover:-translate-y-px
						group-hover:opacity-90 [&_svg]:h-3.5 [&_svg]:w-3.5"
				>
					<Add />
				</span>
				<NavTooltip label="New Chat" />
			</button>
			{enabledItems.map((item) => {
				const isActive =
					item.name === 'chat'
						? currentSpaceType === null
						: currentSpaceType === item.name;
				const Icon = item.icon;

				return (
					<button
						key={item.name}
						ref={(el) => {
							if (el) buttonRefs.current.set(item.name, el);
							else buttonRefs.current.delete(item.name);
						}}
						onClick={() => handleNavClick(item.name)}
						aria-label={item.label}
						data-testid={`nav-${item.name}`}
						className={navBtnClass(isActive)}
					>
						<span className="relative inline-flex">
							<Icon />
							{item.name === 'chat' &&
								currentSpaceType !== null &&
								hasUnreadThreads && (
									<span
										className="bg-beacon-unread absolute -top-1.5 -right-1.5 h-2
											w-2 rounded-full ring-2 ring-white"
									/>
								)}
						</span>
						<NavTooltip label={item.label} />
					</button>
				);
			})}
			<div className="flex-1" />
			<a
				href="mailto:bridgeway-support@adobe.com"
				aria-label="Help"
				data-testid="nav-help"
				className={navBtnClass(false)}
			>
				<HelpCircle />
				<NavTooltip label="Help" />
			</a>
			<button
				ref={(el) => {
					if (el) buttonRefs.current.set('settings', el);
					else buttonRefs.current.delete('settings');
				}}
				onClick={() => handleNavClick('settings')}
				aria-label={
					squareNeedsAttention
						? 'Settings — Square needs attention'
						: 'Settings'
				}
				data-testid="nav-settings"
				className={navBtnClass(currentSpaceType === 'settings')}
			>
				{/* Wayfinding, not the message itself: the explanation and the Reconnect
				    control live together in Settings, so this only has to point there. Same
				    recipe as the unread-chat dot above. */}
				<span className="relative inline-flex">
					<Settings />
					{squareNeedsAttention && (
						<span
							data-testid="nav-settings-attention"
							className="bg-beacon-unread absolute -top-1.5 -right-1.5 h-2 w-2
								rounded-full ring-2 ring-white"
						/>
					)}
				</span>
				<NavTooltip
					label={
						squareNeedsAttention
							? 'Settings — Square needs attention'
							: 'Settings'
					}
				/>
			</button>
		</nav>
	);
};
